import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import BrandLogo from '@/components/BrandLogo';
import { 
  Monitor, LogIn, ArrowRight, CheckCircle, 
  Menu, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [billingCycle, setBillingCycle] = useState('quarterly');
  const [staffCount, setStaffCount] = useState('50');
  const [pricingPlans, setPricingPlans] = useState({});

  const staffOptions = ['50', '100', '200', '300', '500', '1000', '2000'];

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get(`${API}/cms/pricing-plans`);
      setPricingPlans(response.data.plans || {});
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
    }
  };

  // Get CMS features for a plan type, with fallback to default features
  const getPlanFeatures = (planType) => {
    if (pricingPlans[planType] && pricingPlans[planType].features && pricingPlans[planType].features.length > 0) {
      return pricingPlans[planType].features.map(f => ({
        text: f.text,
        bold: f.is_bold || false,
        disabled: !f.is_included
      }));
    }
    // Return default features if CMS not loaded
    return null;
  };

  // Pricing based on staff count and billing cycle
  const getPricing = (planType, staff, cycle) => {
    const basePrices = {
      core: { '50': 8000, '100': 14000, '200': 24000, '300': 32000, '500': 48000, '1000': 88000, '2000': 160000 },
      advanced: { '50': 10500, '100': 18500, '200': 31500, '300': 42000, '500': 63000, '1000': 115500, '2000': 210000 }
    };
    
    const monthlyPrice = basePrices[planType]?.[staff] || basePrices[planType]['50'];
    
    if (cycle === 'quarterly') return { price: monthlyPrice * 3, period: 'Quarterly' };
    if (cycle === 'halfyearly') return { price: monthlyPrice * 6 * 0.95, period: 'Half-yearly' }; // 5% discount
    if (cycle === 'annually') return { price: monthlyPrice * 12 * 0.9, period: 'Annually' }; // 10% discount
    return { price: monthlyPrice, period: 'Month' };
  };

  const plans = [
    {
      name: "Core plan",
      planType: "core",
      description: "Supports up to 10% concurrent users",
      features: [
        { text: "Pre loaded study materials", bold: true },
        { text: "Certification by chapter", bold: false },
        { text: "Scorecard", bold: false },
        { text: "Activity tracking", bold: false },
        { text: "Assessment analysis report", bold: false },
        { text: "Strengths and weaknesses", bold: false },
        { text: "Email-based tech support", bold: true },
        { text: "HTMS AI assist", bold: true },
        { text: "Hospital's own training materials", bold: true, disabled: true },
        { text: "Customer success manager", bold: true, disabled: true },
        { text: "Integrations", bold: true, disabled: true }
      ]
    },
    {
      name: "Advanced plan",
      planType: "advanced",
      description: "Supports up to 10% concurrent users",
      features: [
        { text: "Pre loaded study materials", bold: true },
        { text: "Certification by chapter", bold: false },
        { text: "Scorecard", bold: false },
        { text: "Activity tracking", bold: false },
        { text: "Assessment analysis report", bold: false },
        { text: "Strengths and weaknesses", bold: false },
        { text: "Email-based tech support", bold: true },
        { text: "HTMS AI assist", bold: true },
        { text: "Hospital's own training materials up to 2GB", bold: true },
        { text: "Customer success manager", bold: true },
        { text: "Integrations", bold: true }
      ],
      popular: true
    },
    {
      name: "Enterprise plan",
      planType: "enterprise",
      description: "Custom solutions for your team",
      isEnterprise: true,
      features: [
        { text: "Pre loaded study materials", bold: true },
        { text: "Certification by chapter", bold: false },
        { text: "Scorecard", bold: false },
        { text: "Activity tracking", bold: false },
        { text: "Assessment analysis report", bold: false },
        { text: "Strengths and weaknesses", bold: false },
        { text: "Email-based tech support", bold: true },
        { text: "HTMS AI assist", bold: true },
        { text: "Hospital's own training materials up to 2GB", bold: true },
        { text: "Customer success manager", bold: true },
        { text: "Integrations", bold: true }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo />
            </div>

            {/* Desktop Navigation & Buttons - All aligned right with equal spacing */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/solutions')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                The solution
              </button>
              <button onClick={() => navigate('/pricing')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-semibold transition-colors border-b-2 border-[#0056B3]">
                Pricing
              </button>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Contact us
              </button>
              <Button 
                onClick={() => navigate('/request-demo')}
                className="bg-[#10B981] hover:bg-[#059669] text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Request for demo
              </Button>
              <Button 
                onClick={() => navigate('/signin')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => navigate('/')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Home
                </button>
                <button onClick={() => navigate('/solutions')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  The solution
                </button>
                <button onClick={() => navigate('/pricing')} className="text-sm text-[#0056B3] font-semibold text-left">
                  Pricing
                </button>
                <button onClick={() => navigate('/contact')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Contact us
                </button>
                <Button 
                  onClick={() => navigate('/request-demo')}
                  className="bg-[#10B981] hover:bg-[#059669] text-white text-sm w-full justify-center"
                >
                  Request for demo
                </Button>
                <Button 
                  onClick={() => navigate('/signin')}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm w-full justify-center"
                >
                  Sign in
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Subchapter */}
      <section className="pt-32 pb-12 px-6 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Pricing
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Find the plan that fits you best - from essential features to a full premium experience, we've got you covered. Choose your ideal pricing option and get started today to unlock unbeatable value!
          </p>
          
          {/* Payment Frequency Toggle */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <span className="text-sm text-gray-600 font-medium">Payment frequency</span>
            <div className="flex bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setBillingCycle('quarterly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'quarterly' ? 'bg-[#0056B3] text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Quarterly
              </button>
              <button
                onClick={() => setBillingCycle('halfyearly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'halfyearly' ? 'bg-[#0056B3] text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Half-yearly
              </button>
              <button
                onClick={() => setBillingCycle('annually')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'annually' ? 'bg-[#0056B3] text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annually
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const pricing = plan.isEnterprise ? null : getPricing(plan.planType, staffCount, billingCycle);
              const cmsFeatures = getPlanFeatures(plan.planType);
              const displayFeatures = cmsFeatures || plan.features;
              
              return (
                <div 
                  key={index}
                  className={`rounded-2xl p-6 border-2 transition-all duration-300 hover:shadow-xl ${
                    plan.popular 
                      ? 'border-[#0056B3] bg-white relative' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 right-4">
                      <span className="bg-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Most popular
                      </span>
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                    
                    {/* Staff Count Selector (for Core and Advanced) */}
                    {!plan.isEnterprise && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-1 mb-2">
                          {staffOptions.map((option) => (
                            <button
                              key={option}
                              onClick={() => setStaffCount(option)}
                              className={`px-2 py-1 text-xs rounded transition-all ${
                                staffCount === option 
                                  ? 'bg-[#0056B3] text-white' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Up to {option}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">{plan.description}</p>
                      </div>
                    )}
                    
                    {/* Enterprise Description */}
                    {plan.isEnterprise && (
                      <div className="mb-4">
                        <p className="text-gray-600 text-sm mb-2">{plan.description}</p>
                        <p className="text-sm text-[#0056B3]">Request your plan here.</p>
                      </div>
                    )}
                    
                    {/* Price Display */}
                    {!plan.isEnterprise && pricing && (
                      <div className="mb-4">
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-gray-900">
                            ₹{pricing.price.toLocaleString('en-IN')}
                          </span>
                          <span className="text-gray-500 text-sm">/ {pricing.period}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => navigate('/request-demo')}
                    className={`w-full py-3 rounded-lg text-sm font-semibold mb-6 ${
                      plan.popular
                        ? 'bg-[#0056B3] hover:bg-[#0056B3]/90 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Get started
                  </Button>

                  <ul className="space-y-3">
                    {displayFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          feature.disabled ? 'text-gray-300' : 'text-green-600'
                        }`} />
                        <span className={`text-sm ${
                          feature.disabled 
                            ? 'text-gray-400 line-through' 
                            : feature.bold 
                              ? 'text-gray-900 font-medium' 
                              : 'text-gray-600'
                        }`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Tax Note */}
                  {!plan.isEnterprise && (
                    <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                      Note: Prices shown above are exclusive of applicable taxes. Taxes will be calculated at checkout.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Subchapter */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#0056B3] to-[#00AEEF] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Not sure which plan is right for you?
          </h2>
          <p className="text-xl mb-10 opacity-90">
            Schedule a demo with our team to discuss your specific requirements and find the perfect solution.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/request-demo')}
              className="bg-white text-[#0056B3] hover:bg-gray-100 text-lg px-8 py-6 rounded-lg"
            >
              <Monitor className="w-5 h-5 mr-2" />
              Schedule a demo
            </Button>
            <Button 
              onClick={() => navigate('/contact')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-lg"
            >
              Contact sales
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PricingPage;
